/**
 * Image utility functions for zoom, variant selection, and pan calculations
 */

import type { ImageVariant, ImageVariantName } from '../../types/asset'
import type { Dimensions } from '../../types/base'

// =============================================================================
// CONSTANTS
// =============================================================================

export const MIN_ZOOM = 1
export const MAX_ZOOM = 4
export const ZOOM_STEP = 0.5

export const ZOOM_PRESETS = [1, 1.5, 2, 2.5, 3, 4]

// =============================================================================
// CONTAINER/IMAGE DIMENSIONS
// =============================================================================

export interface ContainerDimensions {
  width: number
  height: number
}

// =============================================================================
// VARIANT SELECTION
// =============================================================================

/**
 * Selects the best image variant based on container size and preferred size.
 * Prioritizes variants that are at least as large as the container to avoid upscaling.
 */
export function selectBestVariant(
  variants: ImageVariant[],
  containerSize: ContainerDimensions,
  preferredSize: ImageVariantName = 'large',
): ImageVariant | undefined {
  if (variants.length === 0) return undefined
  if (variants.length === 1) return variants[0]

  // Priority order for variants
  const priority: ImageVariantName[] = ['original', 'large', 'medium', 'thumbnail']
  const preferredIndex = priority.indexOf(preferredSize)

  // Sort variants by preferred order starting from preferred size
  const sortedByPreference = [...variants].sort((a, b) => {
    const aIdx = priority.indexOf(a.name)
    const bIdx = priority.indexOf(b.name)

    // Prefer variants at or above preferred size
    const aAbove = aIdx <= preferredIndex
    const bAbove = bIdx <= preferredIndex

    if (aAbove && !bAbove) return -1
    if (!aAbove && bAbove) return 1

    // Among same category, prefer smaller (closer to preferred)
    return aIdx - bIdx
  })

  // Find the best variant that's at least as large as the container
  const suitableVariant = sortedByPreference.find(
    (v) => v.dimensions.width >= containerSize.width && v.dimensions.height >= containerSize.height,
  )

  // If no suitable variant, return the largest available
  if (!suitableVariant) {
    return [...variants].sort(
      (a, b) =>
        b.dimensions.width * b.dimensions.height - a.dimensions.width * a.dimensions.height,
    )[0]
  }

  return suitableVariant
}

/**
 * Gets the URL for a specific variant or falls back to the best available.
 */
export function getVariantUrl(
  variants: ImageVariant[],
  variantName: ImageVariantName,
): string | undefined {
  const variant = variants.find((v) => v.name === variantName)
  return variant?.url?.url
}

// =============================================================================
// ZOOM CALCULATIONS
// =============================================================================

/**
 * Calculates the zoom level needed to fit the image within the container.
 */
export function calculateFitZoom(
  imageDimensions: Dimensions,
  containerDimensions: ContainerDimensions,
  padding = 0,
): number {
  const availableWidth = containerDimensions.width - padding
  const availableHeight = containerDimensions.height - padding

  const widthRatio = availableWidth / imageDimensions.width
  const heightRatio = availableHeight / imageDimensions.height

  // Use the smaller ratio to ensure the entire image fits
  return Math.min(widthRatio, heightRatio, 1) // Cap at 1 to not zoom out by default
}

/**
 * Calculates the zoom level to fill the container (may crop).
 */
export function calculateFillZoom(
  imageDimensions: Dimensions,
  containerDimensions: ContainerDimensions,
): number {
  const widthRatio = containerDimensions.width / imageDimensions.width
  const heightRatio = containerDimensions.height / imageDimensions.height

  // Use the larger ratio to fill the container
  return Math.max(widthRatio, heightRatio)
}

/**
 * Clamps zoom level to valid range.
 */
export function clampZoom(zoom: number, minZoom = MIN_ZOOM, maxZoom = MAX_ZOOM): number {
  return Math.min(maxZoom, Math.max(minZoom, zoom))
}

/**
 * Increments zoom by step.
 */
export function incrementZoom(
  currentZoom: number,
  step = ZOOM_STEP,
  maxZoom = MAX_ZOOM,
): number {
  return clampZoom(currentZoom + step, MIN_ZOOM, maxZoom)
}

/**
 * Decrements zoom by step.
 */
export function decrementZoom(
  currentZoom: number,
  step = ZOOM_STEP,
  minZoom = MIN_ZOOM,
): number {
  return clampZoom(currentZoom - step, minZoom, MAX_ZOOM)
}

// =============================================================================
// PAN CALCULATIONS
// =============================================================================

/**
 * Clamps pan values to ensure the image stays within bounds.
 * Returns clamped panX and panY values.
 */
export function clampPan(
  panX: number,
  panY: number,
  zoom: number,
  imageDimensions: Dimensions,
  containerDimensions: ContainerDimensions,
): { panX: number; panY: number } {
  // At zoom = 1, no panning should be allowed
  if (zoom <= 1) {
    return { panX: 0, panY: 0 }
  }

  // Calculate the scaled image dimensions
  const scaledWidth = imageDimensions.width * zoom
  const scaledHeight = imageDimensions.height * zoom

  // Calculate maximum pan distances
  // The image can pan by half the difference between scaled image and container
  const maxPanX = Math.max(0, (scaledWidth - containerDimensions.width) / 2)
  const maxPanY = Math.max(0, (scaledHeight - containerDimensions.height) / 2)

  return {
    panX: Math.max(-maxPanX, Math.min(maxPanX, panX)),
    panY: Math.max(-maxPanY, Math.min(maxPanY, panY)),
  }
}

/**
 * Calculates the center point offset for zooming towards a specific point.
 */
export function calculateZoomOffset(
  currentZoom: number,
  newZoom: number,
  currentPanX: number,
  currentPanY: number,
  pointX: number, // Point to zoom towards (relative to container center)
  pointY: number,
): { panX: number; panY: number } {
  if (currentZoom === newZoom) {
    return { panX: currentPanX, panY: currentPanY }
  }

  const zoomRatio = newZoom / currentZoom
  const newPanX = pointX - (pointX - currentPanX) * zoomRatio
  const newPanY = pointY - (pointY - currentPanY) * zoomRatio

  return { panX: newPanX, panY: newPanY }
}

// =============================================================================
// FORMAT HELPERS
// =============================================================================

/**
 * Formats zoom for display (e.g., "100%", "150%").
 */
export function formatZoom(zoom: number): string {
  return `${Math.round(zoom * 100)}%`
}
